// BatteryStationOverlay — Wave 14cm
// ==================================
//
// Render-Wrapper für die VL-Akku-Stations-Anzeige auf der Karte.
//
// Verantwortung:
//   1. mapBus subscribieren (Map-Instanz live tracken — Rebuild-resilient)
//   2. Stationen fetchen (useBatteryStations) wenn VL aktiv ist
//   3. Marker-Layer + Detail-Sheet rendern
//
// MapPage rendert <BatteryStationOverlay /> einmal innerhalb der Map-JSX,
// alles weitere managed das Component intern. Wenn der User kein VL ist,
// kommt früh ein null-Return → Null-Overhead für Non-VL-User.

import { useEffect, useMemo, useState } from 'react';
import type { Map as MLMap } from 'maplibre-gl';
import { mapBus } from '../lib/map/mapBus';
import { useVlMembership } from '../lib/vlMembership';
import { usePoiBatteryStation } from '../lib/usePois';
import {
  useBatteryStations,
  type BatteryStation,
} from '../lib/batteryStations';
import { BatteryStationLayer } from './BatteryStationLayer';
import { BatteryStationSheet } from './BatteryStationSheet';

/**
 * Wave 14cn: Optional onStationTap override. Wenn gesetzt, wird beim
 * Tap auf eine Station-Pille NICHT das Detail-Sheet geöffnet — stattdessen
 * ruft das Component den Callback auf. CreateCustomPage nutzt das um eine
 * Station als Ziel-Pin zu setzen (statt zu reservieren).
 *
 * Wave 14cw: Optional highlightedAt — Array von Pin-Koordinaten ([lon,lat]).
 * Stationen deren Coords mit einem Pin matchen (exact equality, da
 * CreateCustomPage setEndPin([st.lon, st.lat]) direkt aus den Station-Coords
 * füllt) kriegen einen Glow-Rahmen, damit der User sieht "ja, die ist als
 * Ziel gesetzt". Für MapPage einfach nicht setzen.
 */
interface OverlayProps {
  onStationTap?: (station: BatteryStation) => void;
  highlightedAt?: Array<[number, number] | null | undefined>;
}

export function BatteryStationOverlay({
  onStationTap,
  highlightedAt,
}: OverlayProps = {}) {
  // Live-Subscription auf die Map-Instanz. Bei Map-Rebuild (z.B.
  // Style-Switch von map-out → satellite) kriegen wir die neue Instanz
  // automatisch und attache'n Marker neu.
  const [map, setMap] = useState<MLMap | null>(mapBus.get());
  useEffect(() => {
    return mapBus.subscribe(setMap);
  }, []);

  const { effective: isVl, simulating } = useVlMembership();
  // Wave 14cn: POI-Toggle gating.
  // Wave 14co: Sichtbarkeit hängt NICHT mehr nur an isVl. Server-side
  // ist der GET-Endpunkt auch für Non-VL erreichbar und liefert
  // demo_visible=1 Stationen. Frontend fetcht immer wenn Toggle an ist.
  // Wave 14cp-fix: `simulating` Flag wird an den Hook gereicht damit
  // der Server-side adminAll-Bypass nicht überlagert (Admin der Simulate
  // OFF hat soll Non-VL-Demo-Sicht sehen, nicht alle Stationen).
  const [poiEnabled] = usePoiBatteryStation();
  const shouldShow = poiEnabled;
  const { stations, refresh } = useBatteryStations(shouldShow, {
    simulateVl: simulating,
  });

  // Aktuell selektierte Station fürs Detail-Sheet. null = Sheet zu.
  const [selected, setSelected] = useState<BatteryStation | null>(null);

  // Wenn die Stations-Liste sich ändert (z.B. Admin hat SoC gesetzt),
  // sync'n wir die Selected-State auf die neue Version dieser Station —
  // damit das Sheet die frischen Slot-SoC-Werte zeigt ohne reopen.
  useEffect(() => {
    if (!selected) return;
    const fresh = stations.find((s) => s.id === selected.id);
    if (fresh && fresh.updatedAt !== selected.updatedAt) {
      setSelected(fresh);
    }
  }, [stations, selected]);

  // Wave 14cw: Highlighted-IDs aus den übergebenen Pin-Coords ableiten.
  // Vergleich mit Epsilon weil Floats von JSON-Roundtrip minimal driften
  // können (sollte selten passieren, aber 1e-7 ≈ 1 cm — safe).
  const highlightedIds = useMemo(() => {
    const out = new Set<string>();
    if (!highlightedAt || highlightedAt.length === 0) return out;
    const EPS = 1e-7;
    for (const st of stations) {
      for (const pin of highlightedAt) {
        if (!pin) continue;
        if (
          Math.abs(st.lon - pin[0]) < EPS &&
          Math.abs(st.lat - pin[1]) < EPS
        ) {
          out.add(st.id);
          break;
        }
      }
    }
    return out;
  }, [stations, highlightedAt]);

  if (!shouldShow) return null;

  return (
    <>
      <BatteryStationLayer
        map={map}
        stations={stations}
        highlightedIds={highlightedIds}
        onSelect={(st) => {
          if (onStationTap) {
            onStationTap(st);
            return;
          }
          setSelected(st);
        }}
      />
      {selected && !onStationTap && (
        <BatteryStationSheet
          station={selected}
          onClose={() => setSelected(null)}
          onMutate={refresh}
        />
      )}
    </>
  );
}
